import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    Popover,
    Box,
    Typography,
    Divider,
    Button,
    LinearProgress,
} from "@mui/material";
import StorageGraph from "./storageGraph";
import { autoDeleteVideoData, getDeviceConfigDetails, getDeviceImage, getDeviceUploadImage, updateDevicesList } from "../redux/actions/service-action";
import { useSelector } from "react-redux";
import { getOfflineTime } from "../utils/methods";
import showToast from "../utils/toast";

const Tabs = ["Device", "Camera", "Recording", "Network"];

const DeviceSettingsPopover = ({ anchorEl, onClose, device }: any) => {
    const open = Boolean(anchorEl);
    const { activeCarDetails } = useSelector((state: any) => state.service);
    const [deviceConfigDetails, setDeviceConfigDetails] = React.useState<any>(null);
    const [deviceImage, setDeviceImage] = React.useState<any>(null);
    const offlineData = getOfflineTime(deviceConfigDetails?.last_seen);
    const storageArr = deviceConfigDetails?.metadata?.storage || [];
    const total = storageArr.reduce((sum: any, s: any) => sum + (s.total_gb || 0), 0);
    const used = storageArr.reduce((sum: any, s: any) => sum + (s.used_gb || 0), 0);
    const percentage = total ? Math.round((used / total) * 100) : 0;

    const [activeTab, setActiveTab] = useState("Device");
    const [activeModel, setActiveModel] = useState<"s" | "m">("s");
    const [storageSummary, setStorageSummary] = useState<any>(null);
    const [cameraSettings, setCameraSettings] = useState<any>(null);
    const [healthSummary, setHealthSummary] = useState<any>(null);
    const [healthCheckedAt, setHealthCheckedAt] = useState<number | null>(null);
    const [, setNowTick] = useState(0);
    const [retentionDays, setRetentionDays] = useState<number | null>(null);
    const tunnelUrl = activeCarDetails?.raw?.tunnel_info?.[0];

    // ---- speed test. The device runs Ookla in a background thread, so we
    // kick it off and poll until a result lands.
    const [speedTest, setSpeedTest] = useState<any>(null);
    const [speedTesting, setSpeedTesting] = useState(false);
    const speedPollRef = React.useRef<any>(null);

    const fetchSpeedStatus = React.useCallback(async () => {
        if (!tunnelUrl) return null;
        try {
            const base = String(tunnelUrl).replace(/\/+$/, "");
            const res = await fetch(`${base}/speedtest/status`);
            const data = await res.json();
            return data;
        } catch {
            return null;
        }
    }, [tunnelUrl]);

    const runSpeedTest = async () => {
        if (!tunnelUrl || speedTesting) return;
        setSpeedTesting(true);
        try {
            const base = String(tunnelUrl).replace(/\/+$/, "");
            await fetch(`${base}/speedtest/start`, { method: "POST" });
        } catch {
            setSpeedTesting(false);
            showToast("Could not start the speed test", "error");
            return;
        }
        let tries = 0;
        speedPollRef.current = setInterval(async () => {
            tries += 1;
            const data = await fetchSpeedStatus();
            if (data && data.status !== "running") {
                clearInterval(speedPollRef.current);
                setSpeedTesting(false);
                setSpeedTest(data);
                if (data.status === "error") {
                    showToast("Speed test failed on the device", "error");
                }
            } else if (tries > 30) {
                clearInterval(speedPollRef.current);
                setSpeedTesting(false);
                showToast("Speed test timed out", "error");
            }
        }, 3000);
    };

    // show whatever the device already has when the tab opens
    useEffect(() => {
        if (activeTab !== "Network" || !tunnelUrl) return;
        let dead = false;
        fetchSpeedStatus().then((data) => {
            if (dead || !data) return;
            if (data.status === "running") setSpeedTesting(true);
            else if (data.status !== "idle") setSpeedTest(data);
        });
        return () => {
            dead = true;
        };
    }, [activeTab, tunnelUrl, fetchSpeedStatus]);

    useEffect(() => {
        return () => {
            if (speedPollRef.current) clearInterval(speedPollRef.current);
        };
    }, []);


    // ---- health, derived. A device we can reach right now is online, no matter
    // how old the backend's last_seen is; otherwise fall back to last_seen.
    const formatDuration = (ms: number) => {
        const mins = Math.max(0, Math.floor(ms / 60000));
        if (mins < 1) return "< 1 Min";
        if (mins < 60) return `${mins} Min`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 48) return `${hrs} Hr ${mins % 60} Min`;
        return `${Math.floor(hrs / 24)} Days`;
    };

    const formatUptime = (seconds: number) => {
        const d = Math.floor(seconds / 86400);
        const h = Math.floor((seconds % 86400) / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
    };

    const backendSeen = deviceConfigDetails?.last_seen
        ? new Date(deviceConfigDetails.last_seen).getTime()
        : null;
    const reachableNow =
        !!healthSummary?.online &&
        healthCheckedAt !== null &&
        Date.now() - healthCheckedAt < 90000;
    const lastSeenMs = reachableNow ? healthCheckedAt : backendSeen;

    const lastSeenText = lastSeenMs
        ? new Date(lastSeenMs).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        })
        : "-";

    const offlineText = reachableNow
        ? "Online"
        : lastSeenMs
            ? formatDuration(Date.now() - lastSeenMs)
            : "-";

    const healthRows: [string, string][] = [
        [
            "Restarts (30 days)",
            healthSummary ? String(healthSummary.restarts_30d) : "-",
        ],
        [
            "Uptime",
            healthSummary ? formatUptime(healthSummary.uptime_seconds) : "-",
        ],
        [
            "Recorded today",
            healthSummary
                ? `${healthSummary.recording_uptime_pct_today}%`
                : "-",
        ],
        ["Last seen", lastSeenText],
        ["Status", offlineText],
    ];

    // live figures straight from the device; fall back to the backend metadata
    const liveTotal = storageSummary
        ? ["internal", "sd_card"].reduce(
            (sum, k) => sum + (storageSummary[k]?.present ? storageSummary[k].total_gb : 0),
            0
        )
        : total;
    const liveUsed = storageSummary
        ? ["internal", "sd_card"].reduce(
            (sum, k) => sum + (storageSummary[k]?.present ? storageSummary[k].used_gb : 0),
            0
        )
        : used;
    const livePercentage = liveTotal ? Math.round((liveUsed / liveTotal) * 100) : 0;

    const getDeviceWiseConfigData = async () => {
        if (!tunnelUrl) return;
        try {
            const res: any = await getDeviceConfigDetails(activeCarDetails?.raw?.uuid);
            if (res) {
                setDeviceConfigDetails(res);
                // console.log("Device Config Details", res);
            }
        } catch (error) {
            console.log("error", error);
        }
    };

    const getDeviceWiseImage = async () => {
        try {
            const payload = deviceConfigDetails?.metadata?.installation?.Installation_images;
            const res: any = await getDeviceImage(payload);
            if (res) {
                setDeviceImage(res?.success);
            }
        } catch (error) {
            console.log("error", error);
        }
    };

    const getAiModelSettings = async () => {
        if (!tunnelUrl) return;
        try {
            const res = await fetch(`${String(tunnelUrl).replace(/\/+$/, "")}/settings/ai-model`);
            const data = await res.json();
            const modelName =
                data?.ai_model_settings?.model_name || "";
            if (modelName.includes("11m")) {
                setActiveModel("m");
                modalFormik.setFieldValue("modelType", "m");
            } else {
                setActiveModel("s");
                modalFormik.setFieldValue("modelType", "s");
            }
        } catch (error) {
            console.log(error);
        }
    };

    const deviceUrl = (path: string) =>
        `${String(tunnelUrl).replace(/\/+$/, "")}${path}`;

    const getStorageSummary = async () => {
        if (!tunnelUrl) return;
        try {
            const res = await fetch(deviceUrl("/storage-summary"));
            if (res.ok) setStorageSummary(await res.json());
        } catch (error) {
            console.log(error);
        }
    };

    const getHealthSummary = async () => {
        if (!tunnelUrl) return;
        try {
            const res = await fetch(deviceUrl("/device/health-summary"));
            if (res.ok) {
                setHealthSummary(await res.json());
                setHealthCheckedAt(Date.now());
            }
        } catch (error) {
            // device unreachable: keep the last summary, offline time keeps growing
        }
    };

    const getCameraSettings = async () => {
        if (!tunnelUrl) return;
        try {
            const res = await fetch(deviceUrl("/settings/camera"));
            if (res.ok) setCameraSettings(await res.json());
        } catch (error) {
            console.log(error);
        }
    };

    const getRetention = async () => {
        if (!tunnelUrl) return;
        try {
            const res = await fetch(deviceUrl("/settings/auto-delete"));
            if (!res.ok) return;
            const data = await res.json();
            const days = Number(data?.auto_delete_settings?.retention_days);
            if (!Number.isNaN(days)) {
                setRetentionDays(days);
                autoDeleteFormik.setFieldValue("autoDeleteDays", days);
            }
        } catch (error) {
            console.log(error);
        }
    };

    // useEffect(() => {
    //     getDeviceWiseImage();
    //     getDeviceWiseConfigData();
    // }, [activeCarDetails]);

    useEffect(() => {
        if (!anchorEl || !device) return;
        const loadData = async () => {
            await getDeviceWiseConfigData();
            await getAiModelSettings();
            await getStorageSummary();
            await getRetention();
            await getCameraSettings();
            await getHealthSummary();
        };
        loadData();
    }, [anchorEl, device]);

    useEffect(() => {
        if (!deviceConfigDetails) return;
        getDeviceWiseImage();
    }, [deviceConfigDetails]);

    // while the drawer is open: re-ping the device and re-read the backend
    // record every 30 s, and tick the clock so "Offline" keeps counting
    useEffect(() => {
        if (!anchorEl || !device) return;
        const id = window.setInterval(() => {
            getHealthSummary();
            getDeviceWiseConfigData();
            setNowTick((n) => n + 1);
        }, 30000);
        return () => window.clearInterval(id);
    }, [anchorEl, device, tunnelUrl]);

    const chipStyle = (active: boolean) => ({
        fontSize: 11,
        px: 1.5,
        py: 0.3,
        borderRadius: "6px",
        border: "1px solid #ddd",
        background: active ? "#fbe9e7" : "#f5f5f5",
        color: active ? "#ff5722" : "#666",
        minWidth: "auto",
    });

    const labelStyle = {
        fontSize: 12,
        color: "#777",
        mb: 0.5,
    };

    const SectionHeader = ({ active }: { active: string }) => (
        <Box
            sx={{
                display: "flex",
                gap: 3,
                alignItems: "center",
                bgcolor: "#f5f5f5",
                p: 1,
                borderRadius: "10px",
                width: "fit-content",
                mb: 1.5,
            }}
        >
            {Tabs?.map((tab) =>
                tab === active ? (
                    <Box
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        sx={{
                            px: 2,
                            py: 0.7,
                            borderRadius: "8px",
                            bgcolor: "#f3e5dc",
                            fontSize: 15,
                            fontWeight: 600,
                            color: "#222",
                            cursor: "pointer",
                        }}
                    >
                        {tab}
                    </Box>
                ) : (
                    <Typography
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#222",
                            cursor: "pointer",
                        }}
                    >
                        {tab}
                    </Typography>
                )
            )}
        </Box>
    );

    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    const formikDevice = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: deviceConfigDetails?.device_name || "",
            installation: "Pole",
            location:
                deviceConfigDetails?.lastlocation?.lat +
                "," +
                deviceConfigDetails?.lastlocation?.lng || "",
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .required("Name is required")
                .min(3, "Minimum 3 characters"),
            installation: Yup.string().required("Select installation"),
            location: Yup.string().required("Location is required"),
        }),
        onSubmit: async (values) => {
            try {
                const uploadRes = await getDeviceUploadImage(selectedFiles);
                if (!uploadRes) {
                    showToast("image upload failed", "e");
                    return;
                }
                await updateDevicesList(
                    activeCarDetails?.raw?.uuid,
                    values,
                    uploadRes.uploaded,
                    deviceConfigDetails
                );
                showToast("Device updated successfully", "s");
                getDeviceWiseConfigData();
            } catch (err) {
                console.error(err);
            }
        }
    });

    const cameraFormik = useFormik({
        initialValues: {
            channels: 1,
            resolution: "720p",
            fps: 25,
            codec: "H.265",
        },
        validationSchema: Yup.object({
            channels: Yup.number().required("Required"),
            resolution: Yup.string().required("Required"),
            fps: Yup.number().required("Required"),
            codec: Yup.string().required("Required"),
        }),
        onSubmit: (values) => {
            console.log("Config API Data:", values);
        },
    });

    const autoDeleteFormik = useFormik({
        initialValues: { autoDeleteDays: 7 },
        validationSchema: Yup.object({
            autoDeleteDays: Yup.number().required("Required"),
        }),
        onSubmit: async ({ autoDeleteDays }) => {
            if (!tunnelUrl) {
                showToast("Device is not reachable", "e");
                return;
            }

            // lowering the window deletes footage on the next cleanup pass
            if (retentionDays !== null && autoDeleteDays < retentionDays) {
                const ok = window.confirm(
                    `This lowers retention from ${retentionDays} to ${autoDeleteDays} days.\n` +
                    `Recordings older than ${autoDeleteDays} days will be deleted and cannot be recovered.\n\nContinue?`
                );
                if (!ok) {
                    autoDeleteFormik.setFieldValue("autoDeleteDays", retentionDays);
                    return;
                }
            }

            try {
                const res = await fetch(deviceUrl("/settings/auto-delete"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        auto_delete_settings: { retention_days: autoDeleteDays },
                    }),
                });
                const data = await res.json();
                if (!res.ok) {
                    showToast(data?.detail || "Failed to update auto delete", "e");
                    return;
                }
                setRetentionDays(autoDeleteDays);
                showToast(`Auto delete set to ${autoDeleteDays} days`, "s");
                // keep the backend copy in step, best effort
                autoDeleteVideoData(activeCarDetails?.raw?.uuid, autoDeleteDays).catch(
                    () => undefined
                );
                getStorageSummary();
            } catch (e) {
                console.error(e);
                showToast("Failed to update auto delete", "e");
            }
        },
    });

    // ================= AI MODEL FORM =================

    const modalFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            modelType: deviceConfigDetails?.ai_model_settings?.model_name?.includes("11m") ? "m" : "s"
        },
        onSubmit: async (values) => {
            try {
                const payload = {
                    ai_model_settings: {
                        model_name:
                            values.modelType === "m"
                                ? "yolo11m_orin_ttc"
                                : "yolo11s_orin_ttc",
                        model_path:
                            values.modelType === "m"
                                ? "/home/papli/Music/Music/yolo11m_736x1280.engine"
                                : "/home/papli/Music/Music/yolo11s_736x1280.engine",
                    },
                };

                const res = await fetch(
                    `${String(tunnelUrl).replace(/\/+$/, "")}/settings/ai-model`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(payload),
                    }
                );

                const data = await res.json();
                showToast("AI Model Updated Successfully", "s");
                getDeviceWiseConfigData();
            } catch (e) {
                showToast("Failed to update AI Model", "e");
            }
        },
    });

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "center", horizontal: "right" }}
            transformOrigin={{ vertical: "center", horizontal: "left" }}
        >
            <Box sx={{ width: 360, p: 0, background: "#E1E1E3AB" }}>
                <form onSubmit={formikDevice.handleSubmit}>
                    <div style={{ background: "#ffffffe5", padding: "0px 10px", zIndex: 9999 }}>
                        <SectionHeader active={activeTab} />
                        {activeTab === "Device" && (
                            <>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: 1,
                                    }}
                                >
                                    <Typography sx={labelStyle}>Name</Typography>

                                    <div style={{ width: "65%" }}>
                                        <input
                                            type="text"
                                            name="name"
                                            placeholder="CCTV Camera Name"
                                            value={formikDevice.values.name || deviceConfigDetails?.device_name}
                                            onChange={formikDevice.handleChange}
                                            onBlur={formikDevice.handleBlur}
                                            style={{
                                                width: "100%",
                                                padding: "6px 8px",
                                                borderRadius: "6px",
                                                border: "1px solid #ddd",
                                                fontSize: "12px",
                                            }}
                                        />
                                        {formikDevice.touched.name && typeof formikDevice.errors.name === "string" && (
                                            <div style={{ color: "red", fontSize: "10px" }}>
                                                {formikDevice.errors.name}
                                            </div>
                                        )}
                                    </div>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: 1,
                                    }}
                                >
                                    <Typography sx={labelStyle}>Installation</Typography>

                                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                                        <Button
                                            sx={chipStyle(formikDevice.values.installation === "Pole")}
                                            onClick={() =>
                                                formikDevice.setFieldValue("installation", "Pole")
                                            }
                                        >
                                            Pole
                                        </Button>

                                        <Button
                                            sx={chipStyle(formikDevice.values.installation === "Dashcam")}
                                            onClick={() =>
                                                formikDevice.setFieldValue("installation", "Dashcam")
                                            }
                                        >
                                            Dashcam
                                        </Button>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: 1,
                                    }}
                                >
                                    <Typography sx={labelStyle}>Location</Typography>

                                    <div style={{ width: "65%" }}>
                                        <input
                                            type="text"
                                            name="location"
                                            placeholder="Coordinates"
                                            value={formikDevice.values.location || deviceConfigDetails?.lastlocation.lat + "," + deviceConfigDetails?.lastlocation.lng}
                                            onChange={formikDevice.handleChange}
                                            onBlur={formikDevice.handleBlur}
                                            style={{
                                                width: "100%",
                                                padding: "6px 8px",
                                                borderRadius: "6px",
                                                border: "1px solid #ddd",
                                                fontSize: "12px",
                                            }}
                                        />
                                        {formikDevice.touched.location && formikDevice.errors.location && (
                                            <div style={{ color: "red", fontSize: "10px" }}>
                                                {formikDevice.errors.location}
                                            </div>
                                        )}
                                    </div>
                                </Box>

                                <Typography sx={labelStyle}>Installation Photos</Typography>
                                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                                    {deviceImage?.length != 0 ?
                                        <>
                                            {deviceImage?.map((value: any, ind: any) => {
                                                return (
                                                    <>
                                                        <img src={value?.signed_url} style={{ width: "100px" }} />
                                                    </>
                                                )
                                            })}
                                        </>
                                        : <Box sx={{ width: 60, height: 50, bgcolor: "#d6d2d2", borderRadius: 1 }} />}
                                </Box>
                                <Typography sx={labelStyle}>Upload Photos</Typography>
                                <input
                                    type="file"
                                    multiple
                                    onChange={(e) => {
                                        setSelectedFiles(Array.from(e.target.files || []));
                                    }}
                                />

                                <Divider sx={{ my: 2 }} />

                                <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                                    Health
                                </Typography>

                                {healthRows.map(([label, value]) => (
                                    <Box
                                        key={label}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            fontSize: 12,
                                            mb: 1.4,
                                        }}
                                    >
                                        <span style={{ color: "#777" }}>{label}</span>
                                        <span
                                            style={{
                                                color: value === "Online" ? "#2e7d32" : "#333",
                                                fontWeight: value === "Online" ? 600 : 400,
                                            }}
                                        >
                                            {value}
                                        </span>
                                    </Box>
                                ))}

                                <button type="submit" className="btn mainButton btn-sm">
                                    Update
                                </button>
                            </>
                        )}
                        <Divider sx={{ my: 2 }} />
                    </div>
                </form>

                {activeTab === "Camera" && (
                    <>
                        {/* ================= CAMERA ================= */}
                        <div style={{ background: "#ffffffe5", padding: "0px 10px" }}>
                            {!cameraSettings && (
                                <Typography sx={{ fontSize: 12, color: "#888", py: 2 }}>
                                    Loading camera details...
                                </Typography>
                            )}

                            {cameraSettings &&
                                Object.entries(cameraSettings.cameras || {}).map(
                                    ([camName, cam]: any) => {
                                        const actual = cam?.actual || {};
                                        const rows: [string, string][] = [
                                            ["Type", String(cam?.type || "-").toUpperCase()],
                                            [
                                                "Resolution",
                                                actual.resolution ||
                                                cameraSettings?.recording?.resolution ||
                                                "-",
                                            ],
                                            [
                                                "FPS",
                                                actual.fps != null
                                                    ? String(actual.fps)
                                                    : String(cameraSettings?.recording?.frame_rate ?? "-"),
                                            ],
                                            ["Video Codec", actual.codec || cam?.codec || "-"],
                                            [
                                                "Bitrate",
                                                actual.bitrate_bps
                                                    ? `${(actual.bitrate_bps / 1000000).toFixed(2)} Mbps`
                                                    : cameraSettings?.recording?.bitrate || "-",
                                            ],
                                            [
                                                "Mode",
                                                cam?.transcode ? "Transcode" : "Passthrough",
                                            ],
                                        ];

                                        return (
                                            <Box key={camName} sx={{ mb: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography sx={labelStyle}>Channel</Typography>
                                                    <Button sx={chipStyle(true)} type="button">
                                                        {camName}
                                                    </Button>
                                                </Box>

                                                {rows.map(([label, value]) => (
                                                    <Box
                                                        key={label}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            mb: 1,
                                                        }}
                                                    >
                                                        <Typography sx={labelStyle}>{label}</Typography>
                                                        <Button sx={chipStyle(true)} type="button">
                                                            {value}
                                                        </Button>
                                                    </Box>
                                                ))}

                                                {cam?.source && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Typography sx={labelStyle}>Source</Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 10,
                                                                color: "#777",
                                                                wordBreak: "break-all",
                                                            }}
                                                        >
                                                            {cam.source}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                <Typography
                                                    sx={{ fontSize: 10, color: "#999", mt: 1 }}
                                                >
                                                    {actual.available
                                                        ? `Measured from the latest recorded segment (${actual.segment_mtime})`
                                                        : "No recording probed yet - showing configured values"}
                                                </Typography>
                                            </Box>
                                        );
                                    }
                                )}

                            <Divider sx={{ my: 2 }} />
                        </div>
                    </>
                )}

                {activeTab === "Recording" && (
                    <>
                        {/* ================= RECORDING ================= */}
                        <div style={{ background: "#ffffffe5", padding: "0px 10px" }}>
                            <Typography sx={labelStyle}>Storage</Typography>

                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <span style={{ color: "#ff5722", fontSize: 12 }}>
                                    {livePercentage}%
                                </span>

                                <span style={{ fontSize: 11, color: "#888" }}>
                                    {retentionDays !== null
                                        ? `Last ${retentionDays} days data available`
                                        : "Retention unknown"}
                                </span>
                            </Box>

                            <LinearProgress
                                variant="determinate"
                                value={livePercentage}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    my: 1,
                                    backgroundColor: "#eee",
                                    "& .MuiLinearProgress-bar": {
                                        backgroundColor:
                                            livePercentage > 80
                                                ? "#f44336"
                                                : livePercentage > 50
                                                    ? "#ff9800"
                                                    : "#4caf50",
                                    },
                                }}
                            />

                            <Typography align="right" sx={{ fontSize: 11, color: "#888" }}>
                                {liveUsed.toFixed(1)} / {liveTotal.toFixed(1)} GB
                            </Typography>

                            {storageSummary && (
                                <Box sx={{ mb: 1 }}>
                                    {[
                                        { key: "internal", label: "Internal" },
                                        { key: "sd_card", label: "SD Card" },
                                    ].map(({ key, label }) => {
                                        const vol = storageSummary[key];
                                        if (!vol?.present) return null;
                                        return (
                                            <Box
                                                key={key}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    fontSize: 11,
                                                    color: "#777",
                                                }}
                                            >
                                                <span>{label}</span>
                                                <span>
                                                    {vol.used_gb} / {vol.total_gb} GB ({vol.used_pct}%)
                                                </span>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            <form onSubmit={autoDeleteFormik.handleSubmit}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: 1,
                                    }}
                                >
                                    <Typography sx={labelStyle}>Auto Delete</Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        {[3, 7, 9, 10].map((d) => (
                                            <Button
                                                key={d}
                                                sx={chipStyle(autoDeleteFormik.values.autoDeleteDays === d)}
                                                onClick={() =>
                                                    autoDeleteFormik.setFieldValue("autoDeleteDays", d)
                                                }
                                            >
                                                {d}
                                            </Button>
                                        ))}
                                    </Box>
                                </Box>
                                <button type="submit" className="btn mainButton btn-sm">
                                    Update
                                </button>
                            </form>
                            <Divider sx={{ my: 4 }} />

                            <form onSubmit={modalFormik.handleSubmit}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: 1,
                                    }}
                                >
                                    <Typography sx={labelStyle}>Model</Typography>

                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <Button
                                            type="button"
                                            sx={chipStyle(activeModel === "s")}
                                            onClick={() => {
                                                setActiveModel("s");
                                                modalFormik.setFieldValue("modelType", "s");
                                            }}
                                        >
                                            Small
                                        </Button>
                                    </Box>
                                </Box>

                                <button type="submit" className="btn mainButton btn-sm">
                                    Update
                                </button>
                            </form>
                        </div>
                    </>
                )}

                {activeTab === "Network" && (
                    <div style={{ background: "#ffffffe5", padding: "0px 10px" }}>
                        <StorageGraph />

                        <Box sx={{ mt: 2, mb: 1 }}>
                            <Button
                                onClick={runSpeedTest}
                                disabled={!tunnelUrl || speedTesting}
                                sx={{
                                    textTransform: "none",
                                    background: "#ff6b00",
                                    color: "#fff",
                                    borderRadius: "16px",
                                    px: 2,
                                    py: 0.5,
                                    fontSize: 13,
                                    "&:hover": { background: "#e65f00" },
                                    "&.Mui-disabled": { background: "#e0e0e0", color: "#9e9e9e" },
                                }}
                            >
                                {speedTesting ? "Testing..." : "Run Speed Test"}
                            </Button>

                            {speedTesting && <LinearProgress sx={{ mt: 1 }} />}

                            <Typography sx={{ mt: 0.75, fontSize: 11, color: "#757575" }}>
                                Takes about 30 seconds and uses the full line, so uploads may stall while it runs.
                            </Typography>

                            {speedTest?.status === "done" && (
                                <Box sx={{ mt: 1.5 }}>
                                    {[
                                        ["Download", `${speedTest.download_mbps} Mbps`],
                                        ["Upload", `${speedTest.upload_mbps} Mbps`],
                                        ["Ping", `${speedTest.ping_ms} ms`],
                                        ["Jitter", `${speedTest.jitter_ms} ms`],
                                        ["Packet loss", `${speedTest.packet_loss}%`],
                                        ["ISP", speedTest.isp || "-"],
                                    ].map(([label, value]) => (
                                        <Box
                                            key={label}
                                            sx={{ display: "flex", justifyContent: "space-between", py: 0.35 }}
                                        >
                                            <Typography sx={{ fontSize: 12, color: "#757575" }}>{label}</Typography>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{value}</Typography>
                                        </Box>
                                    ))}
                                    <Typography sx={{ mt: 0.5, fontSize: 11, color: "#9e9e9e" }}>
                                        Last run {speedTest.at?.replace("T", " ")}
                                    </Typography>
                                </Box>
                            )}

                            {speedTest?.status === "error" && (
                                <Typography sx={{ mt: 1, fontSize: 12, color: "#d32f2f" }}>
                                    Failed: {speedTest.error}
                                </Typography>
                            )}
                        </Box>
                    </div>
                )}
            </Box>
        </Popover>
    );
};

export default DeviceSettingsPopover;